import type { BuilderLocationSettings, BuilderPricingVisibilitySettings } from "api-types/builders/builderTypes";
import type { ClientAddress } from "api-types/common/commonResponses";
import type { ClientCommunityWithLevel } from "api-types/communities/communityBaseTypes";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { getValidChildTypes } from "api-types/locations/locationDisplay";
import type { LocationLevelType } from "api-types/locations/locationLevelTypes";
import type { ClientLocationHydrated } from "api-types/locations/locationsResponses";
import stringApplySearch from "common/utils/stringApplySearch";
import type { Breadcrumb } from "lib/components/navigation/BreadcrumbMenu";
import { locationHomePlansPath, locationsPath } from "lib/routeHelpers";
import { getLocationWithAncestors } from "lib/storage/locationStorage";

export function getLocationBreadcrumbs(
  parents: ClientCommunityWithLevel[] | undefined,
  communityId: string | undefined,
  builderId: string,
  accessibleLocationIds?: Set<string>,
) {
  const crumbs: Breadcrumb[] = [];

  if (communityId) {
    // order locations by parent
    const locationsById = Object.fromEntries((parents || []).map((l) => [l.id, l]));
    let current = locationsById[communityId];
    while (current) {
      crumbs.push({
        text: current.name,
        ...(accessibleLocationIds === undefined || accessibleLocationIds.has(current.id)
          ? { href: locationHomePlansPath(builderId, current.id) }
          : {}),
      });
      if (!current.parentId) {
        break;
      }
      current = locationsById[current.parentId];
    }
  }

  crumbs.push({
    text: "All Locations",
    href: locationsPath(builderId),
  });

  return crumbs.reverse();
}

/**
 * Creates a map of parent location IDs to their child locations
 * @param locations - Array of locations
 * @returns A record mapping of parent IDs to arrays of child locations
 */

export function createDescendantMap(locations: ClientLocationHydrated[]): Record<string, ClientLocationHydrated[]> {
  const map: Record<string, ClientLocationHydrated[]> = {};

  for (const location of locations) {
    if (!location.parentId) {
      continue; // Skip this iteration, continue to next item
    }

    if (!map[location.parentId]) {
      map[location.parentId] = [];
    }

    map[location.parentId].push(location);
  }

  return map;
}

/**
 * Extracts searchable text fields from a location
 * @param location - Location to extract text from
 * @returns Array of non-null string values to search through
 */

export function buildLocationSearchableText(location: ClientLocationHydrated): string[] {
  return [location.name, location.city, location.state, location.zip].filter((value): value is string =>
    Boolean(value),
  );
}

/**
 * Filters locations based on archived status
 * @param locations - Locations to filter
 * @param showArchived - Whether to include archived locations
 * @returns Filtered array of locations
 */
export function filterArchivedLocations(
  locations: ClientLocationHydrated[],
  showArchived: boolean,
): ClientLocationHydrated[] {
  if (showArchived) {
    return locations;
  }

  return locations.filter((location) => !location.archivedAt);
}

/**
 * Determines if a location matches a search term directly
 * @param location - Location to check
 * @param searchTerm - Term to search for
 * @returns True if location matches the search term
 */
export function locationMatchesSearch(location: ClientLocationHydrated, searchTerm: string): boolean {
  return stringApplySearch(buildLocationSearchableText(location), searchTerm);
}

/**
 * Recursively checks if a location or any of its descendants match a search term
 * @param location - Location to check
 * @param searchTerm - Term to search for
 * @param descendantMap - Map of location IDs to children
 * @param showArchived - Whether to consider archived locations
 * @returns True if location or any descendants match
 */
export function locationOrDescendantsMatchSearch(
  location: ClientLocationHydrated,
  searchTerm: string,
  descendantMap: Record<string, ClientLocationHydrated[]>,
  showArchived: boolean,
): boolean {
  // Check if location directly matches search
  if (locationMatchesSearch(location, searchTerm)) {
    return true;
  }

  // Get visible children based on archived status
  const children = (descendantMap[location.id] ?? []).filter((child) => showArchived || !child.archivedAt);

  // Recursively check if any children match
  return children.some((child) => locationOrDescendantsMatchSearch(child, searchTerm, descendantMap, showArchived));
}

/**
 * Calculates which locations should be expanded based on search results
 * @param locations - All locations to analyze
 * @param searchTerm - Current search term
 * @param descendantMap - Map of location IDs to children
 * @param showArchived - Whether to consider archived locations
 * @returns Record mapping location IDs to boolean expansion state
 */
export function calculateExpansionMap(
  locations: ClientLocationHydrated[],
  searchTerm: string,
  descendantMap: Record<string, ClientLocationHydrated[]>,
  showArchived: boolean,
): Record<string, boolean> {
  if (!searchTerm) {
    return {};
  }

  const expansionMap: Record<string, boolean> = {};
  const matchingMap: Record<string, boolean> = {};

  // Recursive function to identify matching locations
  function processLocation(location: ClientLocationHydrated): boolean {
    // Skip archived locations if not showing them
    if (location.archivedAt && !showArchived) {
      return false;
    }

    // Check direct match
    const directMatch = locationMatchesSearch(location, searchTerm);

    if (directMatch) {
      matchingMap[location.id] = true;
      return true;
    }

    // Check children based on archived status
    const children = (descendantMap[location.id] ?? []).filter((child) => showArchived || !child.archivedAt);

    const childrenMatch = children.some(processLocation);

    if (childrenMatch) {
      matchingMap[location.id] = true;
      // if the location is a parent of a matching child, it should be expanded
      expansionMap[location.id] = true;
    }

    return directMatch || childrenMatch;
  }

  // Process all locations
  const filteredLocations = filterArchivedLocations(locations, showArchived);
  for (const location of filteredLocations) {
    processLocation(location);
  }

  return expansionMap;
}

/**
 * Determines if a location has expandable children
 * @param location - Location to check
 * @param descendantMap - Map of location IDs to children
 * @param searchTerm - Current search term (optional)
 * @param showArchived - Whether to consider archived locations
 * @returns True if the location can be expanded
 */
export function canExpandLocation(
  location: ClientLocationHydrated,
  descendantMap: Record<string, ClientLocationHydrated[]>,
  searchTerm: string = "",
  showArchived: boolean,
): boolean {
  // Get all direct children

  const allChildren = descendantMap[location.id] ?? [];

  // Filter based on archived status
  const visibleChildren = showArchived ? allChildren : allChildren.filter((child) => !child.archivedAt);

  // If no visible children, can't expand
  if (visibleChildren.length === 0) {
    return false;
  }

  // If not searching, just check if it has visible children
  if (!searchTerm) {
    return visibleChildren.length > 0;
  }

  // When searching, check if any visible children match the search term
  return visibleChildren.some((child) =>
    locationOrDescendantsMatchSearch(child, searchTerm, descendantMap, showArchived),
  );
}

/**
 * Filters locations based on search term and archived status
 * @param locations - Locations to filter
 * @param searchTerm - Term to search for
 * @param descendantMap - Map of location IDs to children
 * @param showArchived - Whether to include archived locations
 * @param forcedExpanded - Map of locations that must be shown (e.g., parents of matching items)
 * @returns Filtered array of locations
 */
export function filterLocationsBySearch(
  locations: ClientLocationHydrated[],
  searchTerm: string,
  descendantMap: Record<string, ClientLocationHydrated[]>,
  showArchived: boolean,
  forcedExpanded?: Record<string, boolean>,
): ClientLocationHydrated[] {
  // If no search, just filter by archived status
  if (!searchTerm) {
    return filterArchivedLocations(locations, showArchived);
  }

  return locations.filter((location) => {
    // Always filter out archived locations if not showing them
    if (location.archivedAt && !showArchived) {
      return false;
    }

    // Keep locations that are forced to be expanded
    if (forcedExpanded && forcedExpanded[location.id]) {
      return true;
    }

    // Otherwise, check if location matches search
    return locationOrDescendantsMatchSearch(location, searchTerm, descendantMap, showArchived);
  });
}

export interface LocationWithHeirarchy {
  location: ClientLocationWithLevel;
  ancestors: ClientLocationWithLevel[];
}

export function locationAncestorsForLocationId(
  locationHierarchy: LocationWithHeirarchy | undefined,
  locationId: string | undefined,
): ClientLocationWithLevel[] | undefined {
  if (locationHierarchy === undefined || locationId === undefined) {
    return undefined;
  }
  if (locationHierarchy.location.id !== locationId) {
    return undefined;
  }
  return locationHierarchy.ancestors;
}
/**
 * Gets a location with its ancestors
 * @param builderId - Builder ID
 * @param locationId - Location ID
 * @returns Location with ancestors (community, division, etc.)
 */
export async function getLocationsWithHierarchy(builderId: string, locationId: string): Promise<LocationWithHeirarchy> {
  const [locationWithAncestors, error] = await getLocationWithAncestors(builderId, locationId);

  if (error) {
    throw new Error(error.message ?? "Failed to get location");
  }

  const [location, ...ancestors] = locationWithAncestors;

  return { location, ancestors };
}

export interface EffectiveLocationAddress {
  address: ClientAddress;
  source: ClientLocationWithLevel;
}

// Mirror of the backend `computeAllLocationAddresses` — see it for the archived-handling divergence. CFG-2266.
export function getEffectiveLocationAddress(
  chain: ReadonlyArray<ClientLocationWithLevel>,
): EffectiveLocationAddress | null {
  for (const loc of chain) {
    if (loc.address) {
      return {
        address: {
          address: loc.address,
          city: loc.city ?? "",
          state: loc.state ?? "",
          zip: loc.zip ?? "",
          country: loc.country ?? "",
        },
        source: loc,
      };
    }
  }
  return null;
}

export interface LocationWithPricingVisibilitySettings {
  pricingVisibilitySettings?: BuilderPricingVisibilitySettings | null;
  builder?: {
    pricingVisibilitySettings?: BuilderPricingVisibilitySettings | null;
  };
}

/**
 * Gets the total number of lots for a location and its descendants
 * @param locations - Locations to get the total number of lots for
 * @returns A map of the location ids to the total number of lots
 */

export function getLocationLotCountWithDescendants(locations: ClientLocationHydrated[]): Record<string, number> {
  const lotsCountByLocation: Record<string, number> = {};

  for (const location of locations) {
    lotsCountByLocation[location.id] = location.lotsCount;
  }

  for (const location of locations) {
    let parentId = location.parentId;
    while (parentId) {
      lotsCountByLocation[parentId] = (lotsCountByLocation[parentId] ?? 0) + location.lotsCount;

      // Ensure we are traversing up the hierarchy and adding the lot count to the parent
      parentId = locations.find((loc) => loc.id === parentId)?.parentId;
    }
  }

  return lotsCountByLocation;
}

/**
 * Gets the total number of home plans for a location and its descendants
 * @param locations - Locations to get the total number of home plans for
 * @returns A map of the location ids to the total number of home plans
 */
export function getLocationHomePlanCountWithDescendants(locations: ClientLocationHydrated[]): Record<string, number> {
  const homePlansCountByLocation: Record<string, number> = {};

  for (const location of locations) {
    homePlansCountByLocation[location.id] = location.homePlanCount;
  }

  for (const location of locations) {
    let parentId = location.parentId;
    while (parentId) {
      homePlansCountByLocation[parentId] = (homePlansCountByLocation[parentId] ?? 0) + location.homePlanCount;

      // Ensure we are traversing up the hierarchy and adding the home plan count to the parent
      parentId = locations.find((loc) => loc.id === parentId)?.parentId;
    }
  }

  return homePlansCountByLocation;
}

/**
 * Whether a location has a visible Sub Locations tab
 * @param locationType - The location's level type
 * @param builderLocationSettings - Builder's location settings for determining enabled types
 * @param hasPermission - Whether the user has VIEW_CHILD_LOCATIONS permission
 * @returns True if valid child types exist and user has permission
 */
export function canViewChildLocations(
  locationType: LocationLevelType | undefined,
  builderLocationSettings: BuilderLocationSettings | null | undefined,
  hasPermission: boolean,
): boolean {
  if (!hasPermission || !locationType) {
    return false;
  }
  return getValidChildTypes(locationType, builderLocationSettings).length > 0;
}

/**
 * The level whose orphan (top-level) locations the "Move Locations" step can adopt under a newly
 * created location: the next enabled level beneath it, skipping disabled middle layers (e.g. a
 * Region with Division disabled yields Community). Returns null when creating under an existing
 * parent, or when nothing is enabled below.
 *
 * This is the candidate level used to look up adoptable orphans; see {@link getMigrationChildLevel}
 * for the final show/hide decision.
 */
export function getMigrationCandidateLevel(
  locationLevelName: LocationLevelType,
  builderLocationSettings: BuilderLocationSettings | null | undefined,
  hasParent: boolean,
): LocationLevelType | null {
  if (hasParent) {
    return null;
  }
  return getValidChildTypes(locationLevelName, builderLocationSettings)[0] ?? null;
}

/**
 * The child level the "Move Locations" step should target, or null when the step should not be
 * shown. The step appears only for super admins creating at the root (a non-null candidate level)
 * when orphan locations actually exist at that level — otherwise it would be empty noise (e.g.
 * introducing a Company above Communities offers orphan Regions, of which there are none).
 */
export function getMigrationChildLevel(
  candidateLevel: LocationLevelType | null,
  isSuperAdmin: boolean,
  orphanCount: number,
): LocationLevelType | null {
  if (!candidateLevel || !isSuperAdmin || orphanCount <= 0) {
    return null;
  }
  return candidateLevel;
}
